import { toast } from "react-toastify";
import { ETHER_ERRORS } from "../constants/ethers-constants";
import type { TransactionError } from "@/types/helpers.types";

let isChainSwitching = false;
let chainSwitchTimeout: NodeJS.Timeout | null = null;

export const setChainSwitching = (switching: boolean) => {
  isChainSwitching = switching;
  if (switching) {
    if (chainSwitchTimeout) clearTimeout(chainSwitchTimeout);
    chainSwitchTimeout = setTimeout(() => {
      isChainSwitching = false;
    }, 3000);
  }
};

export const handleTransactionError = (
  error: TransactionError | unknown,
): void => {
  if (typeof error === "string") {
    const lower = error.toLowerCase();
    if (
      lower.includes("user denied") ||
      lower.includes("user rejected") ||
      lower.includes("user canceled") ||
      lower.includes("user cancelled") ||
      lower.includes("action_rejected") ||
      lower.includes("rejected")
    ) {
      toast.error("User denied transaction");
      return;
    }
    toast.error(error.length > 80 ? "Transaction failed" : error);
    return;
  }

  const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : undefined;

  const asErrorCode = (value: unknown): number | string | undefined =>
    typeof value === "number" || typeof value === "string" ? value : undefined;

  const err = asRecord(error);

  let errorCode: number | string | undefined = asErrorCode(err?.code);
  const errorMessage =
    typeof err?.message === "string" ? (err.message as string) : undefined;

  const info = asRecord(err?.info);

  // Handle nested error structures often returned by Ethers/MetaMask
  const infoError = asRecord(info?.error);
  const nestedInfoErrorCode = asErrorCode(infoError?.code);
  if (nestedInfoErrorCode !== undefined) {
    errorCode = nestedInfoErrorCode;
  } else {
    const directError = asRecord(err?.error);
    const nestedDirectErrorCode = asErrorCode(directError?.code);
    if (nestedDirectErrorCode !== undefined) {
      errorCode = nestedDirectErrorCode;
    }
  }

  // Normalize error message for text matching
  const lowerMsg = errorMessage?.toLowerCase() || "";

  const extractRevertReason = (): string | undefined => {
    const directReason =
      typeof err?.reason === "string" ? err.reason : undefined;
    if (directReason) return directReason;

    const revertRec = asRecord(err?.revert);
    const revertArgs = Array.isArray(revertRec?.args)
      ? revertRec?.args
      : undefined;
    const firstArg = revertArgs?.[0];
    if (typeof firstArg === "string" && firstArg.trim()) return firstArg.trim();

    const parseFrom = (msg: string): string | undefined => {
      const m1 = msg.match(/execution reverted(?::\s*)?"([^"]+)"/i);
      if (m1?.[1]) return m1[1].trim();
      const m2 = msg.match(/reverted(?::\s*)?"([^"]+)"/i);
      if (m2?.[1]) return m2[1].trim();
      const m3 = msg.match(/reason="?([^")]+)"?/i);
      if (m3?.[1]) return m3[1].trim();
      return undefined;
    };

    const nested =
      typeof infoError?.message === "string"
        ? infoError.message
        : typeof asRecord(err?.error)?.message === "string"
          ? (asRecord(err?.error)?.message as string)
          : undefined;
    const parsedNested = nested ? parseFrom(nested) : undefined;
    if (parsedNested) return parsedNested;

    const parsedPrimary = errorMessage ? parseFrom(errorMessage) : undefined;
    if (parsedPrimary) return parsedPrimary;

    const shortMessage =
      typeof err?.shortMessage === "string" ? err.shortMessage : undefined;
    if (shortMessage) return parseFrom(shortMessage) ?? shortMessage;

    return undefined;
  };

  const revertReason = extractRevertReason();
  const normalizeForToast = (msg: string): string => {
    const trimmed = msg.trim();
    if (!trimmed) return "Transaction failed";
    if (trimmed.length > 140) return trimmed.slice(0, 140);
    return trimmed;
  };

  // Check for User Rejection (Code 4001 or "ACTION_REJECTED" or text match)
  if (
    errorCode === 4001 ||
    errorCode === "ACTION_REJECTED" ||
    lowerMsg.includes("user denied") ||
    lowerMsg.includes("user rejected") ||
    lowerMsg.includes("user canceled") ||
    lowerMsg.includes("user cancelled")
  ) {
    toast.error("User denied transaction");
    return;
  }

  if (error instanceof Error) {
    console.error("MAIN ERROR IS THIS", error);

    if (errorCode === ETHER_ERRORS.NETWORK_ERROR && isChainSwitching) {
      console.log("Network error suppressed during chain switch");
      return;
    }

    if (
      errorCode === "CALL_EXCEPTION" ||
      lowerMsg.includes("execution reverted") ||
      lowerMsg.includes("call_exception") ||
      revertReason
    ) {
      toast.error(normalizeForToast(revertReason || "Transaction reverted"));
      return;
    }

    if (
      errorCode === ETHER_ERRORS.INSUFFICIENT_FUNDS ||
      lowerMsg.includes("insufficient funds") ||
      lowerMsg.includes("insufficient balance")
    ) {
      toast.error("Insufficient funds");
      return;
    }

    if (errorCode === ETHER_ERRORS.UNPREDICTABLE_GAS_LIMIT) {
      toast.error(
        normalizeForToast(
          revertReason || "Transaction likely to fail (gas estimation failed)",
        ),
      );
      return;
    }

    if (errorCode === ETHER_ERRORS.NETWORK_ERROR) {
      toast.error("Network error, please try again");
      return;
    }

    if (errorCode === -32603) {
      const nestedMessage =
        typeof infoError?.message === "string"
          ? infoError.message
          : typeof asRecord(err?.error)?.message === "string"
            ? (asRecord(err?.error)?.message as string)
            : undefined;
      const best = revertReason || nestedMessage || errorMessage;
      toast.error(normalizeForToast(best || "Transaction failed"));
      return;
    }

    toast.error(
      normalizeForToast(revertReason || errorMessage || "Transaction failed"),
    );
  } else {
    // Fallback for non-Error objects that weren't caught by the initial check
    console.error("Unknown error:", error);
    toast.error("An unknown error occurred");
  }
};
